import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react'
import type { AddFromFilterRequest } from '../../types/case-container'
import { addFromFilter, clearMapSet } from '../../services/map-set'
import { logger } from '../../utils/logger'
import type { PendingChanges } from './use-map-set-sync'

export interface MapSetBulk {
  addFiltered: (filter: AddFromFilterRequest) => Promise<void>
  clearAll: () => Promise<void>
}

interface UseMapSetBulkArgs {
  caseId: string | null
  setEndpointIds: Dispatch<SetStateAction<Set<string>>>
  pendingChanges: MutableRefObject<PendingChanges>
  debounceTimer: MutableRefObject<NodeJS.Timeout | null>
  setSyncing: (value: boolean) => void
  setError: (value: string | null) => void
}

export function useMapSetBulk({
  caseId,
  setEndpointIds,
  pendingChanges,
  debounceTimer,
  setSyncing,
  setError,
}: UseMapSetBulkArgs): MapSetBulk {
  const addFiltered = useCallback(
    async (filter: AddFromFilterRequest) => {
      if (!caseId) return
      setSyncing(true)
      setError(null)
      try {
        const result = await addFromFilter(caseId, filter)
        setEndpointIds(new Set(result.endpointIds))
        pendingChanges.current = { add: new Set(), remove: new Set() }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add filtered items')
      } finally {
        setSyncing(false)
      }
    },
    [caseId, setEndpointIds, pendingChanges, setSyncing, setError]
  )

  const clearAll = useCallback(async () => {
    if (!caseId) return
    setEndpointIds(new Set())
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    pendingChanges.current = { add: new Set(), remove: new Set() }
    setSyncing(true)
    setError(null)
    try {
      await clearMapSet(caseId)
    } catch (err) {
      logger.warn('useMapSet', 'Failed to sync clear map set to backend', err)
      setError(err instanceof Error ? err.message : 'Failed to clear map set')
    } finally {
      setSyncing(false)
    }
  }, [caseId, setEndpointIds, pendingChanges, debounceTimer, setSyncing, setError])

  return { addFiltered, clearAll }
}
